import { useQuery } from "@apollo/client";
import { Button } from "components/base/Button";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import { SupplierStatusChain } from "components/base/Status";
import { useModals } from "ModalProvider";
import { Link } from "react-router-dom";
import { FETCH_SUPPLIERS } from "./graphql";
import SupplierForm from "./SupplierForm";

const SUPPLIER_CLASS_EMOJI = {
  1: "①",
  2: "②",
  3: "③",
};

function formatSupplierClass(supplierClass) {
  if (
    supplierClass === null ||
    supplierClass === undefined ||
    supplierClass === ""
  ) {
    return "❓";
  }

  return SUPPLIER_CLASS_EMOJI[supplierClass] || `[${supplierClass}]`;
}

const SupplierOnboardingPage = () => {
  const { loading, error, data } = useQuery(FETCH_SUPPLIERS, {
    variables: {
      forOnboarding: true,
      statesIn: [
        "DRAFT",
        "PENDING",
        "VERIFIED",
        "COMPLETED_ONBOARDING",
        "REJECTED",
      ],
    },
  });
  const modal = useModals();

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const suppliers = [...data.suppliers].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );

  function tryAddNewSupplier() {
    modal.present({
      title: "Create New Supplier",
      children: <SupplierForm hide={modal.hide} />,
      isBeingPresented: true,
    });
  }

  return (
    <Page
      staticHeader
      backTo="./.."
      className="h-screen"
      title="Supplier Onboarding"
      rightButtons={
        <div className="flex items-center space-x-6">
          <a
            href="https://qiniu-waboba.cors.tech/waboba-nda-template.docx"
            download="NDA_Template.docx"
          >
            Download NDA Template
          </a>
          <Button onClick={tryAddNewSupplier}>+ New Supplier Onboarding</Button>
        </div>
      }
    >
      <div className="m-6 px-1 opacity-70">
        To add new supplier to our system, please create a new onboarding
        request first. Our team will review the information and contact the
        supplier for NDA signing and further verification. Once the supplier is
        verified, you can proceed to create supplier companies linked to our
        systems (e.g., Odoo) for actual usage. If you need any assistance,
        please contact the operations team.
      </div>

      <div className="card p-0 flex-1 overflow-auto whitespace-nowrap m-6 mt-0">
        <table>
          <thead>
            <tr className="sticky top-0 z-10 backdrop-blur bg-gray-50 bg-opacity-80">
              <th className="py-3 px-4">Supplier</th>
              <th className="">Status</th>
              <th className="text-right">Created by</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map((i) => (
              <tr
                key={i.id}
                className="border-y border-gray-100 dark:border-gray-700"
              >
                <td className="px-4">
                  <div className="flex items-center space-x-1">
                    <Link
                      to={`${i.id}`}
                      className="inline-flex items-center gap-1"
                    >
                      <span
                        className={
                          SUPPLIER_CLASS_EMOJI[i.supplierClass]
                            ? "text-base leading-none"
                            : undefined
                        }
                      >
                        {formatSupplierClass(i.supplierClass)}
                      </span>
                      {i.nickName}
                    </Link>
                    <span className="opacity-70">W#{i.id}</span>
                  </div>
                </td>

                <td className="w-full">
                  <SupplierStatusChain status={i.state} inline />
                </td>
                <td className="text-right">{i.creator.email}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Page>
  );
};

export default SupplierOnboardingPage;
